<template>
  <div class="page max-w-[1010px]">
    <div class="header-container">
      <div class="header">Favorites</div>
      <Button
        variant="outlined"
        class="add-to-favorites-button"
        :raised="isHovered"
        @mouseover="isHovered = true"
        @mouseleave="isHovered = false"
        @click="() => (showAddAFavoriteDialog = true)"
        data-test="add-to-favorites-button"
      >
        <i class="far fa-plus-circle"></i>
        Add to Favorites
      </Button>
    </div>
    <div
      v-if="favoriteStore.favorites && favoriteStore.favorites.length > 0"
      class="flex options-bar items-center"
      data-test="filter-options"
    >
      <Filter
        :showSearch="true"
        :showDateFilter="false"
        :showFrequencyFilter="false"
        :showStatusFilter="false"
        :showSortFilter="true"
        :sortOptions="sortOptions"
        @update:modelValue="searchBy"
        @applyFilters="sortBy"
      />
    </div>
    <div
      v-if="favoriteStore.favorites && favoriteStore.favorites.length == 0 && !blankFavoritesCheck"
      class="flex options-bar justify-center"
      data-test="skeleton-loader"
    >
      <Skeletor
        height="32px"
        class="skeletonTag rounded-lg mb-4 mt-4 md:mt-0"
        width="286px"
      />
    </div>
    <!-- Fundraiser Cards -->
    <div
      v-if="blankFavoritesCheck && !apiError"
      class="flex justify-center text-center"
      data-test="no-favorites-message"
    >
      <div class="favoriteBrowseOrgContainer">
        <b class="blank-title">You haven’t added any Favorite Causes yet.</b>
        <p>Find the causes that matter most to you on our organization search page!</p>
        <div class="flex justify-center pt-4">
          <Button
            variant="outlined"
            class="browse-organizations-button"
            :raised="isHovered"
            @mouseover="isHovered = true"
            @mouseleave="isHovered = false"
            @click="redirectToCMS"
          >
            Browse Organizations
          </Button>
        </div>
      </div>
    </div>
    <div
      v-if="apiError"
      class="flex justify-center text-center"
      data-test="api-error-message"
    >
      <div class="favoriteBrowseOrgContainer">
        <b class="blank-title">Failed to load favorite causes.</b>
        <p>Please try again later.</p>
      </div>
    </div>

    <div
      class="favorite-cards-container"
      data-test="favorite-cards-container"
    >
      <div
        v-if="favoriteStore.favorites && favoriteStore.favorites.length == 0 && !blankFavoritesCheck"
        class="flex flex-col md:flex-row"
      >
        <Skeletor
          v-for="(skeleton, index) in 3"
          :key="index"
          height="368px"
          class="skeletonTag rounded-lg mb-8 md:mb-0 md:mr-8"
          width="288px"
        />
      </div>
      <FavoriteCard
        v-else
        v-for="favorite in filteredFavorites"
        :key="favorite.organizations_id"
        :favorite="favorite"
      />
    </div>
    <div
      v-if="favoriteStore.favorites && favoriteStore.favorites.length > 0"
      class="flex justify-center text-center max-w-[1010px]"
      data-test="browse-more-message"
    >
      <div class="favoriteBrowseOrgContainer">
        <p>
          Looking to find more causes or donate to a favorite organization? Visit our organization search page to find
          the causes that matter most to you and fill your donation cart!
        </p>
        <div class="flex justify-center pt-4">
          <Button
            variant="outlined"
            class="browse-organizations-button"
            :raised="isHovered"
            @mouseover="isHovered = true"
            @mouseleave="isHovered = false"
            @click="redirectToCMS"
          >
            Browse Organizations
          </Button>
        </div>
      </div>
    </div>
    <AddAFavoriteDialog
      v-if="showAddAFavoriteDialog"
      @close="showAddAFavoriteDialog = false"
      :dropdownSelectedValue="dropdownSelectedValue"
      data-test="add-favorite-dialog"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import FavoriteCard from '@/components/favorites/FavoriteCard.vue';
import AddAFavoriteDialog from '@/components/favorites/AddAFavoriteDialog.vue';
import { useFavoriteStore } from '@/stores/FavoriteStore.js';
import { useDonorStore } from '@/stores/DonorStore.js';
import Filter from '@/components/data/Filter.vue';

const isHovered = ref(false);

const favoriteStore = useFavoriteStore();
const donorStore = useDonorStore();
const apiError = ref(false);

const fetchFavorites = async () => {
  try {
    await favoriteStore.fetchFavorites();
    apiError.value = false;
  } catch (error) {
    apiError.value = true;
    console.error('Error fetching favorites:', error); // Log the error for debugging
    // Optionally, rethrow the error or handle it more gracefully
    throw error;
  } finally {
    // You can add any cleanup logic here if necessary
  }
};

const blankFavoritesCheck = computed(() => {
  return !favoriteStore.favoritesLoading && favoriteStore.favorites.length === 0;
});

const showAddAFavoriteDialog = ref(false);

const sortOptions = computed(() => [
  { label: 'Most Recent', value: 'mostRecent' },
  { label: 'Organization: A to Z', value: 'organizationAToZ' },
  { label: 'Organization: Z to A', value: 'organizationZToA' },
]);

const redirectToCMS = () => {
  window.location.href = donorStore.domain.replace(/\/$/, '') + '/organizations?is_organization=true';
};

const search = ref('');
const sort = ref('mostRecent');

const searchBy = (val) => {
  if (val != undefined) {
    search.value = val;
  } else {
    search.value = '';
  }
};

const sortBy = (filters) => {
  if (filters.selectedSort != undefined) {
    sort.value = filters.selectedSort.value;
  } else {
    sort.value = 'mostRecent';
  }
};

const filteredFavorites = computed(() => {
  let filtered = favoriteStore.favorites;
  if (search.value) {
    filtered = filtered.filter((favorite) => {
      return favorite.organization.organizationName.toLowerCase().includes(search.value.toLowerCase());
    });
  }

  if (sort.value === 'organizationAToZ') {
    filtered = filtered.sort((a, b) => {
      return a.organization.organizationName.localeCompare(b.organization.organizationName);
    });
  } else if (sort.value === 'organizationZToA') {
    filtered = filtered.sort((a, b) => {
      return b.organization.organizationName.localeCompare(a.organization.organizationName);
    });
  } else if (sort.value === 'mostRecent') {
    filtered = filtered.sort((a, b) => {
      return new Date(b.organization.enteredDate) - new Date(a.organization.enteredDate);
    });
  } else if (sort.value === 'leastRecent') {
    filtered = filtered.sort((a, b) => {
      return new Date(a.organization.enteredDate) - new Date(b.organization.enteredDate);
    });
  }

  return filtered;
});

const dropdownSelectedValue = ref(null);

onMounted(() => {
  fetchFavorites();
});
</script>

<style>
.add-to-favorites-button {
  color: #000;
  margin-bottom: 8px;
}
.browse-organizations-button {
  color: #000;
}
.favorite-cards-container {
  display: flex;
  flex-wrap: wrap;
  gap: 20px 32px;
  margin-bottom: 100px;
  max-width: 930px;
}
.favoriteBrowseOrgContainer {
  max-width: 450px;
}
.favoriteBrowseOrgContainer .header {
  font-size: 20px;
}
.options-bar {
  justify-content: space-between;
}
@media (max-width: 1024px) {
  .favorite-cards-container {
    justify-content: center; /* Centers cards horizontally */
    margin-right: 0; /* Removes any right margin that might push content to the left */
    padding: 0 20px; /* Adds padding on the sides */
  }
}
@media (max-width: 768px) {
  .favorite-cards-container {
    justify-content: center; /* Centers cards horizontally */
    margin-right: 0; /* Removes any right margin that might push content to the left */
    padding: 0 20px; /* Adds padding on the sides */
  }
  .options-bar {
    flex-direction: column;
    align-items: center;
  }
}
</style>
